import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { List, X, CalendarStar } from "@phosphor-icons/react";
import { useBooking } from "@/context/booking";

const LINKS = [
  ["About", "about"], ["Services", "services"], ["Events", "events"],
  ["Gallery", "gallery"], ["Reviews", "testimonials"], ["Contact", "contact"],
];

const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

export const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { openBooking } = useBooking();

  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", on);
    return () => window.removeEventListener("scroll", on);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-500 ${scrolled ? "bg-black/70 backdrop-blur-xl border-b border-white/10" : "bg-transparent"}`}
    >
      <div className="max-w-7xl mx-auto px-5 md:px-8 flex items-center justify-between h-20">
        <button data-testid="logo-home" onClick={() => scrollTo("top")} className="text-left leading-none">
          <span className="font-display italic text-2xl md:text-3xl gold-text">Epic Memories</span>
          <span className="hidden sm:block text-[10px] tracking-[0.3em] text-zinc-400 uppercase">Little Rock · Arkansas</span>
        </button>

        <nav className="hidden lg:flex items-center gap-8">
          {LINKS.map(([label, id]) => (
            <button key={id} data-testid={`nav-${id}`} onClick={() => scrollTo(id)}
              className="link-underline text-sm font-medium text-zinc-200 hover:text-white uppercase tracking-wide">
              {label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button data-testid="nav-tickets-btn" onClick={() => scrollTo("events")}
            className="hidden md:inline-flex items-center gap-2 text-sm font-semibold text-white border border-white/25 rounded-full px-4 py-2 hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors">
            <CalendarStar size={16} weight="fill" /> Tickets
          </button>
          <button data-testid="nav-book-btn" onClick={() => openBooking()}
            className="hidden sm:inline-flex bg-[#D4AF37] text-black text-sm font-bold rounded-full px-5 py-2.5 hover:bg-[#F3E5AB] transition-colors">
            Book Your Event
          </button>
          <button data-testid="mobile-menu-toggle" className="lg:hidden text-white" onClick={() => setOpen(!open)}>
            {open ? <X size={26} /> : <List size={26} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div data-testid="mobile-menu"
            initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}
            className="lg:hidden overflow-hidden bg-black/95 backdrop-blur-xl border-t border-white/10">
            <div className="px-6 py-6 flex flex-col gap-4">
              {LINKS.map(([label, id]) => (
                <button key={id} onClick={() => { scrollTo(id); setOpen(false); }}
                  className="text-left text-lg font-heading font-bold text-zinc-100">{label}</button>
              ))}
              <button data-testid="mobile-book-btn" onClick={() => { openBooking(); setOpen(false); }}
                className="mt-2 bg-[#D4AF37] text-black font-bold rounded-full px-5 py-3">Book Your Event</button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export const FloatingBook = () => {
  const { openBooking } = useBooking();
  return (
    <motion.button
      data-testid="floating-book-btn"
      onClick={() => openBooking()}
      initial={{ scale: 0, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ delay: 1.2, type: "spring" }}
      whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}
      className="fixed bottom-6 right-6 z-50 bg-[#D4AF37] text-black font-heading font-bold text-sm rounded-full px-6 py-4 shadow-[0_10px_40px_rgba(212,175,55,0.4)] flex items-center gap-2">
      <CalendarStar size={20} weight="fill" /> Book Now
    </motion.button>
  );
};
